// src/components/Forum/CreatePostModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPost } from '../../services/forumApi';
import { classifyCategoryFromDescription } from '../../services/smartSearchApi';
import { getJwtToken } from '../../utils/auth';
import { useAuth } from '../../utils/AuthContext';
import { useLocations } from '../../utils/LocationContext';
import { useCategories } from '../../utils/CategoriesContext';
import { uploadFileWithMetadata } from '../../storageService';
import { toast } from 'react-toastify';
import { Save, X, MessageSquare, Hash, MapPin, Phone, Camera, Bell, Plus, Clock, ShieldCheck, Eye, ChevronLeft, ChevronRight, CheckCircle2, Sparkles } from 'lucide-react';
import './CreatePostModal.css';

// Constantes para validación de imágenes
const MAX_FILE_SIZE = 8 * 1024 * 1024; // 8MB
const VALID_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/bmp'];
const MAX_POST_IMAGES = 2;

const CreatePostModal = ({ isOpen, onClose, categoryId = null, categoryName = '', onPostCreated }) => {
    const navigate = useNavigate();
    const { selectedGlobalLocation, locations: prefetchedLocations } = useLocations();
    const { serviceCategories } = useCategories();
    const { userData } = useAuth();

    // Estado para controlar los pasos del modal
    // 1 = introducción, 2 = "Tu pedido" (qué necesitás), 3 = "Cómo te contactamos", 4 = preview
    const [currentStep, setCurrentStep] = useState(1);
    const FORM_STEPS = [
        { key: 'what', label: 'Tu pedido' },
        { key: 'how', label: 'Contacto' },
        { key: 'review', label: 'Revisar' }
    ];

    const [formData, setFormData] = useState({
        description: '',
        categoryIds: categoryId ? [categoryId] : [], // Solo una categoría
        locationId: '', // Se inicializa vacío y se setea cuando las locations estén disponibles
        phone: '',
        phoneConsent: false,
        preferredVisitTime: '',
        images: [] // Array para paths de imágenes
    });

    const VISIT_TIME_OPTIONS = [
        'Mañanas (9-12)',
        'Tardes (12-18)',
        'Noche (18-21)',
        'Fines de semana',
        'Cualquier momento'
    ];

    // Estados para manejo de imágenes
    const [imageFiles, setImageFiles] = useState([]); // Files seleccionados
    const [imagePreviewUrls, setImagePreviewUrls] = useState([]); // URLs de preview
    const [uploadingImages, setUploadingImages] = useState(false);

    // Categorías y locations precargadas desde los contexts (prefetch en login/app start)
    const categories = serviceCategories;
    const locations = prefetchedLocations;
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    // Estado para inferencia de categoría con Vertex AI (solo cuando no hay categoryId preseleccionado).
    // Umbral calibrado para text-embedding-multilingual: matches semánticamente correctos
    // suelen caer en 0.55-0.80 con el texto enriquecido actual.
    const AI_CATEGORY_CONFIDENCE_THRESHOLD = 0.75;
    const [aiSuggested, setAiSuggested] = useState(null); // { categoryId, categoryName, similarity }
    const [aiClassifying, setAiClassifying] = useState(false);
    const lastClassifiedDescriptionRef = useRef('');
    const formInitializedRef = useRef(false);

    // Función para determinar la ubicación por defecto
    const determineDefaultLocation = (availableLocations) => {
        if (!availableLocations || availableLocations.length === 0) return '';

        console.log('🔧 determineDefaultLocation iniciando...');
        console.log('📋 Locations disponibles:', availableLocations.map(loc => ({ id: loc.id, name: loc.name })));

        // Detectar si estamos en el caso donde los IDs son undefined pero los nombres existen
        const idsAreUndefined = availableLocations.every(loc => loc.id === undefined);
        console.log('⚠️ IDs son undefined, usando names como IDs:', idsAreUndefined);

        // Función helper para obtener el "ID efectivo" (id real o name como fallback)
        const getEffectiveId = (location) => {
            return location.id !== undefined ? location.id : location.name;
        };

        // Prioridad 1: LocationContext (ubicación global seleccionada)
        if (selectedGlobalLocation) {
            console.log('🔍 Analizando selectedGlobalLocation:', selectedGlobalLocation);

            // Si los IDs son undefined, buscar por nombre directamente
            if (idsAreUndefined && selectedGlobalLocation.name) {
                const matchByName = availableLocations.find(loc => loc.name === selectedGlobalLocation.name);
                if (matchByName) {
                    console.log('✅ Coincidencia por name (IDs undefined):', matchByName.name);
                    return getEffectiveId(matchByName);
                }
            }

            // Caso 1A: Coincidencia exacta por ID (si los IDs son válidos)
            if (!idsAreUndefined && selectedGlobalLocation.id) {
                const matchById = availableLocations.find(loc => loc.id === selectedGlobalLocation.id);
                if (matchById) {
                    console.log('✅ Coincidencia exacta por ID:', matchById.name);
                    return getEffectiveId(matchById);
                }
            }

            // Caso 1B: Coincidencia exacta por name
            if (selectedGlobalLocation.name) {
                const matchByName = availableLocations.find(loc => loc.name === selectedGlobalLocation.name);
                if (matchByName) {
                    console.log('✅ Coincidencia exacta por name:', matchByName.name);
                    return getEffectiveId(matchByName);
                }
            }

            // Caso 1C: Si selectedGlobalLocation.id es en realidad un nombre
            if (selectedGlobalLocation.id && typeof selectedGlobalLocation.id === 'string') {
                const matchByIdAsName = availableLocations.find(loc => loc.name === selectedGlobalLocation.id);
                if (matchByIdAsName) {
                    console.log('✅ selectedGlobalLocation.id era un name:', selectedGlobalLocation.id);
                    return getEffectiveId(matchByIdAsName);
                }
            }

            // Caso 1D: Coincidencia por zone si existe
            if (selectedGlobalLocation.zone) {
                const matchByZone = availableLocations.find(loc =>
                    loc.name === selectedGlobalLocation.zone ||
                    loc.id === selectedGlobalLocation.zone
                );
                if (matchByZone) {
                    console.log('✅ Coincidencia por zone:', selectedGlobalLocation.zone);
                    return getEffectiveId(matchByZone);
                }
            }

            // Caso 1E: Búsqueda case-insensitive por nombre
            if (selectedGlobalLocation.name) {
                const matchCaseInsensitive = availableLocations.find(loc =>
                    loc.name && loc.name.toLowerCase() === selectedGlobalLocation.name.toLowerCase()
                );
                if (matchCaseInsensitive) {
                    console.log('✅ Coincidencia case-insensitive:', selectedGlobalLocation.name);
                    return getEffectiveId(matchCaseInsensitive);
                }
            }

            console.log('⚠️ No se encontró coincidencia para selectedGlobalLocation');
        }

        // Prioridad 2: Ubicación del perfil del usuario
        if (userData?.location) {
            console.log('🔍 Analizando userData.location:', userData.location);

            // Caso 2A: userData.location es un objeto con id (y los IDs son válidos)
            if (!idsAreUndefined && userData.location.id) {
                const matchById = availableLocations.find(loc => loc.id === userData.location.id);
                if (matchById) {
                    console.log('✅ Usando userData.location.id:', matchById.name);
                    return getEffectiveId(matchById);
                }
            }

            // Caso 2B: userData.location es un objeto con name
            if (userData.location.name) {
                const matchByName = availableLocations.find(loc => loc.name === userData.location.name);
                if (matchByName) {
                    console.log('✅ Encontrado por userData.location.name:', matchByName.name);
                    return getEffectiveId(matchByName);
                }
            }
        }

        // Prioridad 3: userData.locationId directo
        if (userData?.locationId) {
            console.log('🔍 Analizando userData.locationId:', userData.locationId);

            // Caso 3A: Coincidencia directa por ID (si los IDs son válidos)
            if (!idsAreUndefined) {
                const matchById = availableLocations.find(loc => loc.id === userData.locationId);
                if (matchById) {
                    console.log('✅ Usando userData.locationId como ID:', matchById.name);
                    return getEffectiveId(matchById);
                }
            }

            // Caso 3B: userData.locationId es un nombre
            const matchByName = availableLocations.find(loc => loc.name === userData.locationId);
            if (matchByName) {
                console.log('✅ userData.locationId era un nombre:', userData.locationId);
                return getEffectiveId(matchByName);
            }
        }

        console.log('⚠️ No se encontró ubicación por defecto válida');
        return '';
    };

    // Efecto principal para inicializar el modal
    useEffect(() => {
        if (isOpen) {
            // Resetear errores y volver al paso inicial
            setErrors({});
            setCurrentStep(1);

            // Resetear sugerencia de IA
            setAiSuggested(null);
            lastClassifiedDescriptionRef.current = '';
        }
    }, [isOpen]);

    // Efecto para resetear y configurar el formulario cuando el modal se abre y las locations están disponibles.
    // Sólo inicializa UNA vez por apertura: si selectedGlobalLocation cambia mientras el modal está abierto,
    // no pisamos lo que el usuario ya escribió (especialmente la descripción).
    useEffect(() => {
        if (!isOpen) {
            formInitializedRef.current = false;
            return;
        }
        if (formInitializedRef.current) return;
        if (locations.length === 0) return;

        const defaultLocationId = determineDefaultLocation(locations);

        setFormData({
            description: '',
            categoryIds: categoryId ? [categoryId] : [],
            locationId: defaultLocationId,
            phone: ''
        });
        formInitializedRef.current = true;

        console.log('🔄 Formulario inicializado con locationId:', defaultLocationId);
    }, [isOpen, locations.length, categoryId, selectedGlobalLocation?.id]);

    // Debug useEffect para monitorear el estado del formulario
    useEffect(() => {
        if (isOpen && locations.length > 0) {
            console.log('🔍 DEBUG DETALLADO:');
            console.log('📍 selectedGlobalLocation completo:', selectedGlobalLocation);
            console.log('📍 selectedGlobalLocation.id:', selectedGlobalLocation?.id);
            console.log('📍 selectedGlobalLocation.name:', selectedGlobalLocation?.name);

            // Detectar si los IDs son undefined
            const idsAreUndefined = locations.every(loc => loc.id === undefined);
            console.log('⚠️ IDs son undefined:', idsAreUndefined);

            console.log('📋 Locations disponibles (primeras 5):');
            locations.slice(0, 5).forEach((loc, i) => {
                const effectiveId = loc.id !== undefined ? loc.id : loc.name;
                console.log(`  ${i + 1}. ID: "${loc.id}" | Name: "${loc.name}" | EffectiveID: "${effectiveId}" | Type: ${typeof loc.id}`);
            });

            console.log('📋 TODAS las locations:');
            console.log('  IDs disponibles:', locations.map(loc => `"${loc.id}"`));
            console.log('  Names disponibles:', locations.map(loc => `"${loc.name}"`));
            console.log('  IDs efectivos:', locations.map(loc => `"${loc.id !== undefined ? loc.id : loc.name}"`));

            console.log('🔄 Estado actual del formulario:');
            console.log('  - locationId actual:', `"${formData.locationId || ''}" (tipo: ${typeof formData.locationId})`);

            if (idsAreUndefined) {
                // Si los IDs son undefined, buscar por name
                console.log('  - ¿LocationId existe por Name?:', formData.locationId ? locations.some(loc => loc.name === formData.locationId) : false);
            } else {
                // Si los IDs son válidos, buscar por ID y name
                console.log('  - ¿LocationId existe por ID?:', formData.locationId ? locations.some(loc => loc.id === formData.locationId) : false);
                console.log('  - ¿LocationId existe por Name?:', formData.locationId ? locations.some(loc => loc.name === formData.locationId) : false);
            }
        }
    }, [formData.locationId, locations.length, isOpen, selectedGlobalLocation]);

    // Bloquear scroll del body cuando el modal está abierto y manejar viewport en móviles
    useEffect(() => {
        if (isOpen) {
            // Bloquear scroll
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';

            // Mejorar viewport para móviles
            const viewport = document.querySelector('meta[name=viewport]');
            let originalContent = '';

            if (viewport) {
                originalContent = viewport.getAttribute('content');
                // Agregar viewport-fit=cover para manejar safe areas en móviles
                const newContent = originalContent.includes('viewport-fit=cover')
                    ? originalContent
                    : originalContent + ', viewport-fit=cover';
                viewport.setAttribute('content', newContent);
            }

            // Función para manejar resize de ventana (útil cuando cambia orientación)
            const handleResize = () => {
                // Forzar recálculo de altura en iOS Safari
                if (window.navigator.userAgent.includes('Safari') &&
                    /iPad|iPhone|iPod/.test(navigator.userAgent)) {
                    const modal = document.querySelector('.forum-create-post-modal-container');
                    if (modal) {
                        modal.style.height = 'auto';
                    }
                }
            };

            window.addEventListener('resize', handleResize);
            window.addEventListener('orientationchange', handleResize);

            return () => {
                // Restaurar scroll
                document.body.style.overflow = 'auto';
                document.body.style.position = '';
                document.body.style.width = '';

                // Restaurar viewport original
                if (viewport && originalContent) {
                    viewport.setAttribute('content', originalContent);
                }

                // Limpiar listeners
                window.removeEventListener('resize', handleResize);
                window.removeEventListener('orientationchange', handleResize);
            };
        }
    }, [isOpen]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'phone') {
            // Para el teléfono, permitir escritura libre pero limitar caracteres
            const cleanValue = value.replace(/[^\d+\-\s()]/g, ''); // Solo números y símbolos de teléfono
            setFormData(prev => ({
                ...prev,
                [name]: cleanValue
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }

        // Limpiar error específico del campo
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Inferir categoría con Vertex AI mientras el usuario escribe la descripción.
    // Solo se ejecuta cuando NO hay categoryId preseleccionado (acceso desde Home).
    // Trigger: 1 segundo de inactividad después de tipear (debounce).
    // Si la similaridad supera el umbral, autoselecciona la categoría.
    useEffect(() => {
        if (categoryId) return; // categoría fija, no inferir
        if (!isOpen) return;
        const desc = formData.description.trim();
        if (desc.length < 10) {
            // Si borraron texto, limpiamos la sugerencia visible
            if (aiSuggested) setAiSuggested(null);
            return;
        }
        if (desc === lastClassifiedDescriptionRef.current) return; // ya clasificada

        const timer = setTimeout(async () => {
            lastClassifiedDescriptionRef.current = desc;
            setAiClassifying(true);
            try {
                const result = await classifyCategoryFromDescription(desc);
                if (result?.match && typeof result.similarity === 'number'
                    && result.similarity >= AI_CATEGORY_CONFIDENCE_THRESHOLD
                    && result.categoryId) {
                    const exists = categories.some(c => c.id === result.categoryId);
                    if (exists) {
                        setFormData(prev => ({ ...prev, categoryIds: [result.categoryId] }));
                        setAiSuggested({
                            categoryId: result.categoryId,
                            categoryName: result.categoryName,
                            similarity: result.similarity
                        });
                        setErrors(prev => prev.categoryIds ? { ...prev, categoryIds: '' } : prev);
                        console.log(`🤖 Categoría inferida por IA: ${result.categoryName} (${(result.similarity * 100).toFixed(1)}%)`);
                    } else {
                        console.warn('⚠️ Categoría sugerida no encontrada en el listado:', result.categoryId);
                        setAiSuggested(null);
                    }
                } else {
                    console.log(`🤖 IA no superó umbral (${result?.similarity != null ? (result.similarity * 100).toFixed(1) + '%' : 'sin resultado'}) — no se autoselecciona.`);
                    setAiSuggested(null);
                }
            } catch (err) {
                console.warn('Error en clasificación IA:', err?.message || err);
                setAiSuggested(null);
            } finally {
                setAiClassifying(false);
            }
        }, 1000);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.description, categoryId, isOpen, categories.length]);

    // Manejar el formateo cuando el usuario termine de escribir el teléfono
    const handlePhoneBlur = (e) => {
        const value = e.target.value;
        if (value.trim() !== '') {
            const formattedPhone = formatPhoneForWhatsApp(value);
            setFormData(prev => ({
                ...prev,
                phone: formattedPhone
            }));
        }
    };

    // Función para formatear teléfono para WhatsApp
    const formatPhoneForWhatsApp = (value) => {
        // Si el campo está vacío, retornar vacío
        if (!value || value.trim() === '') {
            return '';
        }

        // Obtener solo los números del valor
        let numericValue = value.replace(/\D/g, '');

        // Casos donde necesitamos limpiar prefijos existentes

        // Si ya tiene formato completo +549XXXXXXXXXX, extraer solo el número local
        if (numericValue.length >= 13 && numericValue.startsWith('549')) {
            numericValue = numericValue.substring(3);
        }
        // Si tiene código de país 54 + 9 + número
        else if (numericValue.length >= 12 && numericValue.startsWith('549')) {
            numericValue = numericValue.substring(3);
        }
        // Si tiene solo código de país 54
        else if (numericValue.length >= 11 && numericValue.startsWith('54') && !numericValue.startsWith('549')) {
            numericValue = numericValue.substring(2);
        }
        // Si comienza con 9 sin el 54
        else if (numericValue.length >= 10 && numericValue.startsWith('9') && !numericValue.startsWith('549')) {
            numericValue = numericValue.substring(1);
        }

        // Remover 0 inicial si existe (códigos de área argentinos)
        if (numericValue.startsWith('0')) {
            numericValue = numericValue.substring(1);
        }

        // Validar que tengamos números para formatear
        if (numericValue.length === 0) {
            return '';
        }

        // Limitar a máximo 10 dígitos (código área + número)
        if (numericValue.length > 10) {
            numericValue = numericValue.substring(0, 10);
        }

        // Formatear para mostrar: agregar +54 9 al inicio para Argentina
        return `+54 9 ${numericValue}`;
    };

    // Función para generar el número limpio para WhatsApp (sin espacios)
    const getCleanWhatsAppNumber = (formattedPhone) => {
        if (!formattedPhone) return '';

        // Remover espacios del formato +54 9 1234567890
        return formattedPhone.replace(/\s/g, '');
    };

    // Validación del paso 2 ("Tu pedido"): descripción + categoría
    const validateStepWhat = () => {
        const newErrors = {};
        if (!formData.description.trim()) {
            newErrors.description = 'La descripción es requerida';
        } else if (formData.description.trim().length < 10) {
            newErrors.description = 'La descripción debe tener al menos 10 caracteres';
        }
        if (formData.categoryIds.length === 0) {
            newErrors.categoryIds = 'Debes seleccionar una categoría';
        }
        setErrors(prev => ({ ...prev, ...newErrors, description: newErrors.description || '', categoryIds: newErrors.categoryIds || '' }));
        return Object.keys(newErrors).length === 0;
    };

    // Validación del paso 3 ("Cómo te contactamos"): ubicación + teléfono + consentimiento
    const validateStepHow = () => {
        const newErrors = {};
        if (!formData.locationId) {
            newErrors.locationId = 'La ubicación es requerida';
        }
        if (!formData.phone || !formData.phone.trim()) {
            newErrors.phone = 'El teléfono es requerido';
        } else {
            const phonePattern = /^\+54\s9\s\d{8,10}$/;
            if (!phonePattern.test(formData.phone.trim())) {
                newErrors.phone = 'Formato de teléfono inválido. Ingresa un número argentino válido.';
            }
        }
        if (!formData.phoneConsent) {
            newErrors.phoneConsent = 'Debés aceptar compartir tu teléfono con los profesionales registrados';
        }
        setErrors(prev => ({
            ...prev,
            locationId: newErrors.locationId || '',
            phone: newErrors.phone || '',
            phoneConsent: newErrors.phoneConsent || ''
        }));
        return Object.keys(newErrors).length === 0;
    };

    const validateForm = () => {
        const okWhat = validateStepWhat();
        const okHow = validateStepHow();
        return okWhat && okHow;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const token = await getJwtToken();
            if (!token) {
                toast.error('Tu sesión ha expirado. Inicia sesión nuevamente.');
                onClose();
                navigate('/login');
                return;
            }

            // Subir imágenes si existen
            let imagePaths = [];
            if (imageFiles.length > 0) {
                setUploadingImages(true);
                try {
                    console.log('📷 Subiendo imágenes al storage...');

                    // Obtener email del usuario desde AuthContext
                    const userEmail = userData?.email;
                    if (!userEmail) {
                        throw new Error('No se pudo obtener el email del usuario');
                    }

                    const userId = getUserIdFromEmail(userEmail);
                    if (!userId) {
                        throw new Error('No se pudo procesar el email del usuario');
                    }

                    console.log('👤 Usuario:', userEmail, '→ userId:', userId);
                    console.log('🔒 Verificando reglas de seguridad para path: forum/posts/' + userId);

                    const imageUploadPromises = imageFiles.map(async (file, index) => {
                        const timestamp = Date.now();
                        const fileExtension = file.name.split('.').pop() || 'jpg';
                        // Estructura: forum/posts/{userId}/{timestamp}_{index}.{extension}
                        const imagePath = `forum/posts/${userId}/${timestamp}_${index}.${fileExtension}`;

                        console.log(`📁 Subiendo imagen ${index + 1}: ${imagePath}`);

                        try {
                            await uploadFileWithMetadata(file, imagePath);
                            console.log(`✅ Imagen ${index + 1} subida exitosamente`);
                            return imagePath;
                        } catch (uploadError) {
                            console.error(`❌ Error subiendo imagen ${index + 1}:`, uploadError);
                            console.error('📍 Path que falló:', imagePath);
                            console.error('👤 Usuario autenticado:', userEmail);
                            console.error('🔑 UserId generado:', userId);
                            throw uploadError;
                        }
                    });

                    imagePaths = await Promise.all(imageUploadPromises);
                    console.log('✅ Todas las imágenes subidas exitosamente:', imagePaths);
                } catch (error) {
                    console.error('Error subiendo imágenes:', error);
                    toast.error('Error al subir las imágenes. Inténtalo nuevamente.');
                    setUploadingImages(false);
                    setLoading(false);
                    return;
                } finally {
                    setUploadingImages(false);
                }
            }

            // Verificar que locationId sea válido
            console.log('🔍 Validando locationId antes de enviar:');
            console.log('  - locationId enviado:', `"${formData.locationId}"`);
            console.log('  - Tipo:', typeof formData.locationId);

            // Detectar si los IDs son undefined
            const idsAreUndefined = locations.every(loc => loc.id === undefined);
            console.log('  - IDs son undefined:', idsAreUndefined);

            let selectedLocation;
            let locationIdToSend;

            if (idsAreUndefined) {
                // Si los IDs son undefined, buscar por name y usar name como ID
                selectedLocation = locations.find(loc => loc.name === formData.locationId);
                locationIdToSend = formData.locationId; // El name será el ID
                console.log('  - Búsqueda por name (IDs undefined):', selectedLocation);
            } else {
                // Si los IDs son válidos, buscar por ID
                selectedLocation = locations.find(loc => loc.id === formData.locationId);
                locationIdToSend = formData.locationId;
                console.log('  - Búsqueda por ID:', selectedLocation);
            }

            if (!selectedLocation) {
                // Intentar buscar por nombre como fallback adicional
                const locationByName = locations.find(loc => loc.name === formData.locationId);
                console.log('  - Búsqueda fallback por nombre:', locationByName);

                if (locationByName) {
                    console.log('⚠️ Encontrado por nombre en fallback');
                    selectedLocation = locationByName;
                    locationIdToSend = idsAreUndefined ? locationByName.name : locationByName.id;
                } else {
                    console.error('❌ LocationId inválido - no encontrado:', formData.locationId);
                    console.log('📋 IDs efectivos disponibles:', locations.map(loc => loc.id !== undefined ? loc.id : loc.name));
                    console.log('📋 Names disponibles:', locations.map(loc => loc.name));
                    toast.error(`Ubicación inválida: "${formData.locationId}". Por favor selecciona una ubicación válida.`);
                    setLoading(false);
                    return;
                }
            }

            // Preparar datos para enviar con la estructura que espera el backend
            const postData = {
                description: formData.description.trim(),
                categoryIds: formData.categoryIds,
                serviceLocationId: locationIdToSend, // Backend espera 'serviceLocationId', no 'locationId'
                authorPhone: getCleanWhatsAppNumber(formData.phone) || undefined, // Número limpio para WhatsApp
                phoneConsent: formData.phoneConsent,
                preferredVisitTime: formData.preferredVisitTime || undefined,
                images: imagePaths, // Array de paths de imágenes
                urgent: false // Campo por defecto, se podría hacer configurable en el futuro
            };

            // Limpiar campos undefined del objeto (el backend podría ser estricto)
            Object.keys(postData).forEach(key => {
                if (postData[key] === undefined || postData[key] === '') {
                    delete postData[key];
                }
            });

            console.log('📤 Enviando post al backend con estructura correcta:', postData);
            console.log('📍 Ubicación seleccionada:', selectedLocation);
            console.log('🔧 Campos enviados:');
            console.log('  - serviceLocationId:', postData.serviceLocationId);
            console.log('  - authorPhone:', postData.authorPhone);
            console.log('  - categoryIds:', postData.categoryIds);
            console.log('  - images:', postData.images);

            await createPost(postData);

            toast.success('¡Post creado exitosamente!');

            // Resetear formulario usando la función para determinar ubicación por defecto
            const defaultLocationId = determineDefaultLocation(locations);
            setFormData({
                description: '',
                categoryIds: categoryId ? [categoryId] : [], // Mantener la categoría si viene predefinida
                locationId: defaultLocationId,
                phone: '',
                phoneConsent: false,
                preferredVisitTime: '',
                images: []
            });

            // Limpiar imágenes
            setImageFiles([]);
            setImagePreviewUrls([]);

            console.log('🔄 Formulario reseteado con locationId:', defaultLocationId);

            // Llamar callback y cerrar modal
            if (onPostCreated) {
                const selectedCategoryId = formData.categoryIds[0];
                const selectedCategory = categories.find(c => c.id === selectedCategoryId);
                onPostCreated({
                    categoryId: selectedCategoryId,
                    categoryName: selectedCategory?.name || categoryName || ''
                });
            }

            onClose();

        } catch (error) {
            console.error('Error creating post:', error);
            if (error.response?.status === 401) {
                toast.error('Tu sesión ha expirado. Inicia sesión nuevamente.');
                // Cerrar modal y redirigir al login
                onClose();
                navigate('/login');
            } else if (error.response?.data?.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error('Error al crear el post. Inténtalo nuevamente.');
            }
        } finally {
            setLoading(false);
        }
    };

    // ¿El usuario escribió algo que se perdería al cerrar el modal?
    const hasUnsavedContent = () => Boolean(
        (formData.description && formData.description.trim()) ||
        (formData.phone && formData.phone.trim()) ||
        (formData.preferredVisitTime && formData.preferredVisitTime.trim()) ||
        (imageFiles && imageFiles.length > 0)
    );

    // Cierra el modal, pidiendo confirmación si hay contenido sin guardar.
    const requestClose = () => {
        if (hasUnsavedContent() && !window.confirm('¿Descartar el post? Se perderá lo que escribiste.')) {
            return;
        }
        setCurrentStep(1);
        onClose();
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            requestClose();
        }
    };

    // Avanza al siguiente paso, validando el paso actual primero (en formularios)
    const goToNextStep = () => {
        if (currentStep === 2) {
            if (!validateStepWhat()) return;
        } else if (currentStep === 3) {
            if (!validateStepHow()) return;
        }
        setCurrentStep(prev => Math.min(prev + 1, 4));
        // Scrollear al tope del body cuando cambia de paso
        setTimeout(() => {
            const body = document.querySelector('.forum-create-post-modal-body, .forum-create-post-modal-intro-content');
            if (body) body.scrollTop = 0;
        }, 0);
    };

    // Vuelve al paso anterior
    const goToPreviousStep = () => {
        setCurrentStep(prev => Math.max(prev - 1, 1));
        setTimeout(() => {
            const body = document.querySelector('.forum-create-post-modal-body, .forum-create-post-modal-intro-content');
            if (body) body.scrollTop = 0;
        }, 0);
    };

    // Cierra el modal (con confirmación si hay contenido sin guardar) y resetea al primer paso
    const handleClose = () => {
        requestClose();
    };

    // Renderiza el indicador de progreso (paso actual / total)
    const renderStepIndicator = () => {
        if (currentStep < 2) return null;
        const activeIndex = currentStep - 2; // 0, 1, 2
        return (
            <div className="forum-create-post-modal-steps" role="progressbar"
                aria-valuemin={1} aria-valuemax={FORM_STEPS.length} aria-valuenow={activeIndex + 1}>
                {FORM_STEPS.map((step, idx) => {
                    const isActive = idx === activeIndex;
                    const isComplete = idx < activeIndex;
                    return (
                        <React.Fragment key={step.key}>
                            <div className={`forum-create-post-modal-step ${isActive ? 'is-active' : ''} ${isComplete ? 'is-complete' : ''}`}>
                                <span className="forum-create-post-modal-step-circle">
                                    {isComplete ? <CheckCircle2 size={16} /> : idx + 1}
                                </span>
                                <span className="forum-create-post-modal-step-label">{step.label}</span>
                            </div>
                            {idx < FORM_STEPS.length - 1 && (
                                <span className={`forum-create-post-modal-step-line ${isComplete ? 'is-complete' : ''}`} />
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        );
    };

    // Validación de archivos de imagen
    const validateImageFile = (file) => {
        if (file.size > MAX_FILE_SIZE) {
            toast.error(`La imagen ${file.name} supera el límite de 8MB.`);
            return false;
        }

        if (!VALID_IMAGE_TYPES.includes(file.type)) {
            toast.error(`La imagen ${file.name} no es de un formato válido.`);
            return false;
        }

        return true;
    };

    // Manejar selección de imágenes
    const handleImageSelection = (files) => {
        const fileArray = Array.from(files);

        if (imageFiles.length + fileArray.length > MAX_POST_IMAGES) {
            toast.error(`Solo puedes subir máximo ${MAX_POST_IMAGES} imágenes por post.`);
            return;
        }

        const validFiles = fileArray.filter(validateImageFile);
        if (validFiles.length === 0) return;

        // Generar URLs de preview
        const newPreviewUrls = validFiles.map(file => URL.createObjectURL(file));

        setImageFiles(prev => [...prev, ...validFiles]);
        setImagePreviewUrls(prev => [...prev, ...newPreviewUrls]);
    };

    // Eliminar imagen
    const removeImage = (index) => {
        // Limpiar URL de preview
        URL.revokeObjectURL(imagePreviewUrls[index]);

        setImageFiles(prev => prev.filter((_, i) => i !== index));
        setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
    };

    // Limpiar URLs de preview al cerrar
    useEffect(() => {
        return () => {
            imagePreviewUrls.forEach(url => URL.revokeObjectURL(url));
        };
    }, [imagePreviewUrls]);

    // Función helper para convertir email en userId válido para paths
    const getUserIdFromEmail = (email) => {
        if (!email) return null;
        // Convertir email a formato seguro para paths de Firebase Storage
        // Usar replace secuencial para manejar múltiples ocurrencias
        let userId = email.toLowerCase();
        // Reemplazar @ primero
        userId = userId.replace('@', '_');
        // Reemplazar todos los . con _
        while (userId.includes('.')) {
            userId = userId.replace('.', '_');
        }
        // Reemplazar + si existe
        userId = userId.replace('+', '_');

        console.log('📧 Email conversion:', email, '→', userId);
        return userId;
    };

    // Función de debug temporal para probar las reglas de Storage
    const debugStorageRules = async () => {
        try {
            const userEmail = userData?.email;
            if (!userEmail) {
                console.log('❌ No hay usuario autenticado');
                return;
            }

            const userId = getUserIdFromEmail(userEmail);
            console.log('🔍 Debug Storage Rules:');
            console.log('  📧 Email original:', userEmail);
            console.log('  🔑 UserID generado:', userId);
            console.log('  📁 Path esperado: forum/posts/' + userId + '/test.jpg');
            console.log('  🔒 Reglas de seguridad esperan:');
            console.log('    - Usuario autenticado: ✅');
            console.log('    - Email no nulo: ✅');
            console.log('    - UserID length > 5:', userId.length > 5 ? '✅' : '❌');
            console.log('    - UserID contains "_":', userId.includes('_') ? '✅' : '❌');

            // Log adicional para verificar token
            const token = await getJwtToken();
            if (token) {
                const payload = JSON.parse(atob(token.split('.')[1]));
                console.log('  🎫 Token JWT email:', payload.email);
                console.log('  🔄 ¿Email coincide?:', payload.email === userEmail ? '✅' : '❌');
            }

        } catch (error) {
            console.error('❌ Error en debug:', error);
        }
    };

    // Exponer función para debugging
    window.debugStorageRules = debugStorageRules;

    if (!isOpen) {
        return null;
    }

    // Renderizar introducción (Paso 1) - Timeline vertical
    const renderIntroduction = () => (
        <div className="forum-create-post-modal-intro forum-intro-v2">
            <div className="forum-create-post-modal-intro-content">
                <div className="forum-intro-hero">
                    <h2 className="forum-intro-hero-title">Conectá con profesionales en minutos</h2>
                    <p className="forum-intro-hero-subtitle">
                        Contás tu problema y se notifica a los profesionales de tu zona. Los primeros cinco interesados pueden escribirte.
                    </p>
                </div>

                <ol className="forum-intro-timeline" role="list">
                    <li className="forum-intro-step">
                        <div className="forum-intro-step-marker">
                            <MessageSquare size={20} strokeWidth={2.2} className="forum-intro-step-icon" />
                        </div>
                        <div className="forum-intro-step-body">
                            <h3>Contás qué necesitás</h3>
                            <p>Describí brevemente el trabajo o servicio que estás buscando.</p>
                        </div>
                    </li>

                    <li className="forum-intro-step">
                        <div className="forum-intro-step-marker">
                            <Hash size={20} strokeWidth={2.2} className="forum-intro-step-icon" />
                        </div>
                        <div className="forum-intro-step-body">
                            <h3>Elegí categoría, zona y preferencias</h3>
                            <p>Para que solo te contacten profesionales que realmente puedan ayudarte.</p>
                        </div>
                    </li>

                    <li className="forum-intro-step">
                        <div className="forum-intro-step-marker">
                            <Bell size={20} strokeWidth={2.2} className="forum-intro-step-icon" />
                        </div>
                        <div className="forum-intro-step-body">
                            <h3>Avisamos a los profesionales</h3>
                            <p>Notificamos a profesionales de tu rubro y ubicación para que vean tu publicación.</p>
                        </div>
                    </li>

                    <li className="forum-intro-step">
                        <div className="forum-intro-step-marker">
                            <Phone size={20} strokeWidth={2.2} className="forum-intro-step-icon" />
                        </div>
                        <div className="forum-intro-step-body">
                            <h3>Te escriben por WhatsApp</h3>
                            <p>Los primeros cinco profesionales que muestren interés acceden a tu teléfono para contactarte.</p>
                        </div>
                    </li>
                </ol>
            </div>

            <div className="forum-create-post-modal-intro-footer">
                <button
                    type="button"
                    className="forum-create-post-modal-btn forum-create-post-modal-btn-cancel"
                    onClick={handleClose}
                >
                    <X size={16} />
                    Cancelar
                </button>
                <button
                    type="button"
                    className="forum-create-post-modal-btn forum-create-post-modal-btn-next"
                    onClick={goToNextStep}
                >
                    <Plus size={16} />
                    Crear mi Post
                </button>
            </div>
        </div>
    );

    // Helper para resolver el nombre de una location
    const findLocationName = () => {
        if (!formData.locationId || locations.length === 0) return '';
        const idsAreUndefined = locations.every(loc => loc.id === undefined);
        const found = idsAreUndefined
            ? locations.find(loc => loc.name === formData.locationId)
            : locations.find(loc => loc.id === formData.locationId);
        return found?.name || '';
    };

    // Helper para resolver el nombre de la categoría seleccionada
    const findCategoryName = () => {
        if (categoryName) return categoryName;
        if (formData.categoryIds.length === 0) return '';
        const id = formData.categoryIds[0];
        const found = categories.find(c => c.id === id);
        return found?.name || '';
    };

    // Header común para los pasos del formulario
    const renderFormHeader = (title) => (
        <div className="forum-create-post-modal-header">
            <h2 className="forum-create-post-modal-title">
                <MessageSquare size={22} />
                {title}
                {categoryName && currentStep === 2 && <span>en {categoryName}</span>}
            </h2>
            <button
                className="forum-create-post-modal-close-btn"
                onClick={handleClose}
                type="button"
                aria-label="Cerrar"
            >
                <X size={18} />
            </button>
        </div>
    );

    // Paso 2: "Tu pedido" — qué necesitás (título, descripción, categoría, imágenes)
    const renderStepWhat = () => (
        <>
            {renderFormHeader('Crear Post')}
            {renderStepIndicator()}
            <div className="forum-create-post-modal-body">
                <div className="forum-create-post-modal-step-intro">
                    <h3 className="forum-create-post-modal-step-title">Contanos qué necesitás</h3>
                    <p className="forum-create-post-modal-step-subtitle">
                        Una buena descripción ayuda a que los profesionales sepan exactamente cómo ayudarte.
                    </p>
                </div>

                {/* Descripción */}
                <div className="forum-create-post-modal-form-group">
                    <label htmlFor="description" className="forum-create-post-modal-label">
                        <MessageSquare size={16} />
                        Descripción <span className="forum-create-post-modal-label-required">*</span>
                    </label>
                    <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        className="forum-create-post-modal-textarea"
                        placeholder="Describí qué necesitás, el problema que tenés o el servicio que buscás. Cuanto más detalle, mejor."
                        maxLength={1000}
                        required
                    />
                    {errors.description && (
                        <div className="forum-create-post-modal-error">{errors.description}</div>
                    )}
                    <div className="forum-create-post-modal-char-count">
                        {formData.description.length}/1000 caracteres
                    </div>
                </div>

                {/* Imágenes */}
                <div className="forum-create-post-modal-form-group">
                    <label className="forum-create-post-modal-label">
                        <Camera size={16} />
                        Imágenes <span className="forum-create-post-modal-label-optional">(opcional)</span>
                    </label>
                    <div className="field-help">
                        {imageFiles.length > 0
                            ? `${imageFiles.length} ${imageFiles.length > 1 ? 'imágenes seleccionadas' : 'imagen seleccionada'}`
                            : 'Agregá imágenes para mostrar el problema o el lugar (máx. 2, 8MB c/u).'}
                    </div>

                    <div className="forum-create-post-modal-image-preview-container">
                        {imagePreviewUrls.map((url, index) => (
                            <div key={index} className="forum-create-post-modal-image-preview">
                                <img src={url} alt={`Preview ${index + 1}`} className="forum-create-post-modal-image" />
                                <button
                                    type="button"
                                    className="forum-create-post-modal-image-remove"
                                    onClick={() => removeImage(index)}
                                    aria-label="Quitar imagen"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        ))}
                    </div>

                    {imageFiles.length < MAX_POST_IMAGES && (
                        <label className="forum-create-post-modal-file-input-label">
                            <input
                                id="images"
                                type="file"
                                name="images"
                                accept="image/*"
                                onChange={(e) => handleImageSelection(e.target.files)}
                                className="forum-create-post-modal-file-input-hidden"
                                multiple
                            />
                            <div className="forum-create-post-modal-file-input-content">
                                <Camera size={22} />
                                <span>Seleccionar imágenes</span>
                                <small>JPG, PNG, WEBP — hasta 8MB cada una</small>
                            </div>
                        </label>
                    )}
                </div>

                {/* Categoría */}
                <div className="forum-create-post-modal-form-group">
                    <label htmlFor="categoryId" className="forum-create-post-modal-label">
                        <Hash size={16} />
                        Categoría <span className="forum-create-post-modal-label-required">*</span>
                        {!categoryId && aiClassifying && (
                            <span className="forum-create-post-modal-ai-status">
                                <span className="forum-create-post-modal-btn-spinner" aria-hidden="true" />
                                Analizando descripción…
                            </span>
                        )}
                        {!categoryId && !aiClassifying && aiSuggested && (
                            <span
                                className="forum-create-post-modal-ai-badge"
                                title={`Inferida automáticamente con ${(aiSuggested.similarity * 100).toFixed(0)}% de confianza. Podés cambiarla.`}
                            >
                                <Sparkles size={12} />
                                Sugerida por IA
                            </span>
                        )}
                    </label>
                    <div className="field-help">
                        {categoryId
                            ? 'Categoría fija basada en la sección donde estás creando el post.'
                            : 'Seleccioná la categoría que mejor aplique a tu pedido.'}
                    </div>

                    {categoryId ? (
                        <div className="forum-categories-chips-container">
                            <div className="forum-category-chip selected disabled">
                                {categoryName}
                            </div>
                        </div>
                    ) : (
                        <select
                            id="categoryId"
                            name="categoryId"
                            value={formData.categoryIds[0] || ''}
                            onChange={(e) => {
                                const value = e.target.value;
                                setFormData(prev => ({
                                    ...prev,
                                    categoryIds: value ? [value] : []
                                }));
                                if (errors.categoryIds) {
                                    setErrors(prev => ({ ...prev, categoryIds: '' }));
                                }
                                // Si el usuario cambia manualmente, ocultar el badge
                                if (aiSuggested && value !== aiSuggested.categoryId) {
                                    setAiSuggested(null);
                                }
                            }}
                            className={`forum-create-post-modal-select ${aiSuggested ? 'is-ai-suggested' : ''}`}
                            required
                        >
                            <option value="">Seleccioná una categoría</option>
                            {categories.map(category => (
                                <option key={category.id} value={category.id}>
                                    {category.name}
                                </option>
                            ))}
                        </select>
                    )}

                    <div className="field-footer">
                        {errors.categoryIds && <span className="error-message">{errors.categoryIds}</span>}
                    </div>
                </div>
            </div>

            <div className="forum-create-post-modal-footer">
                <button
                    type="button"
                    className="forum-create-post-modal-btn forum-create-post-modal-btn-back"
                    onClick={goToPreviousStep}
                    disabled={loading}
                >
                    <ChevronLeft size={16} />
                    Volver
                </button>
                <button
                    type="button"
                    className="forum-create-post-modal-btn forum-create-post-modal-btn-next"
                    onClick={goToNextStep}
                    disabled={loading}
                >
                    Continuar
                    <ChevronRight size={16} />
                </button>
            </div>
        </>
    );

    // Paso 3: "Cómo te contactamos" — ubicación, horario, teléfono, consentimiento
    const renderStepHow = () => (
        <>
            {renderFormHeader('Crear Post')}
            {renderStepIndicator()}
            <div className="forum-create-post-modal-body">
                <div className="forum-create-post-modal-step-intro">
                    <h3 className="forum-create-post-modal-step-title">¿Cómo te contactamos?</h3>
                    <p className="forum-create-post-modal-step-subtitle">
                        Estos datos son para que los profesionales cercanos puedan llegar a vos.
                    </p>
                </div>

                {/* Ubicación */}
                <div className="forum-create-post-modal-form-group">
                    <label htmlFor="locationId" className="forum-create-post-modal-label">
                        <MapPin size={16} />
                        Ubicación <span className="forum-create-post-modal-label-required">*</span>
                    </label>
                    <div className="field-help">Seleccioná la zona donde necesitás el servicio.</div>
                    <select
                        id="locationId"
                        name="locationId"
                        value={formData.locationId || ''}
                        onChange={(e) => {
                            const newLocationId = e.target.value;
                            setFormData(prev => ({ ...prev, locationId: newLocationId }));
                            if (errors.locationId) {
                                setErrors(prev => ({ ...prev, locationId: '' }));
                            }
                        }}
                        className="forum-create-post-modal-select"
                        required
                    >
                        <option value="">Seleccioná tu ubicación</option>
                        {locations.map(location => {
                            const effectiveId = location.id !== undefined ? location.id : location.name;
                            return (
                                <option key={location.name} value={effectiveId}>
                                    {location.name}
                                </option>
                            );
                        })}
                    </select>
                    <div className="field-footer">
                        {errors.locationId && <span className="error-message">{errors.locationId}</span>}
                    </div>
                </div>

                {/* Horario preferido (opcional) */}
                <div className="forum-create-post-modal-form-group">
                    <label htmlFor="preferredVisitTime" className="forum-create-post-modal-label">
                        <Clock size={16} />
                        Horario preferido <span className="forum-create-post-modal-label-optional">(opcional)</span>
                    </label>
                    <select
                        id="preferredVisitTime"
                        name="preferredVisitTime"
                        value={formData.preferredVisitTime}
                        onChange={(e) => setFormData(prev => ({ ...prev, preferredVisitTime: e.target.value }))}
                        className="forum-create-post-modal-select"
                    >
                        <option value="">Sin preferencia</option>
                        {VISIT_TIME_OPTIONS.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>
                    <div className="forum-create-post-modal-hint">
                        Ayuda a los profesionales a saber cuándo te conviene que te contacten.
                    </div>
                </div>

                {/* Teléfono */}
                <div className="forum-create-post-modal-form-group">
                    <label htmlFor="phone" className="forum-create-post-modal-label">
                        <Phone size={16} />
                        Teléfono de contacto <span className="forum-create-post-modal-label-required">*</span>
                    </label>
                    <input
                        id="phone"
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        onBlur={handlePhoneBlur}
                        className="forum-create-post-modal-input"
                        placeholder="Ej: 11 1234-5678"
                        maxLength={25}
                        required
                    />
                    {errors.phone && (
                        <div className="forum-create-post-modal-error">{errors.phone}</div>
                    )}
                    <div className="forum-create-post-modal-hint">
                        Solo los profesionales que muestren interés podrán contactarte a este número.
                    </div>
                </div>

                {/* Consentimiento */}
                <div className="forum-create-post-modal-form-group">
                    <label
                        htmlFor="phoneConsent"
                        className={`forum-create-post-modal-consent ${errors.phoneConsent ? 'has-error' : ''} ${formData.phoneConsent ? 'is-checked' : ''}`}
                    >
                        <input
                            id="phoneConsent"
                            type="checkbox"
                            checked={formData.phoneConsent}
                            onChange={(e) => {
                                setFormData(prev => ({ ...prev, phoneConsent: e.target.checked }));
                                if (errors.phoneConsent) {
                                    setErrors(prev => ({ ...prev, phoneConsent: '' }));
                                }
                            }}
                            className="forum-create-post-modal-consent-checkbox"
                        />
                        <span className="forum-create-post-modal-consent-text">
                            <ShieldCheck size={14} className="forum-create-post-modal-consent-icon" />
                            Acepto compartir mi número de teléfono con los profesionales registrados que muestren interés en mi publicación, para que puedan contactarme.
                        </span>
                    </label>
                    {errors.phoneConsent && (
                        <div className="forum-create-post-modal-error">{errors.phoneConsent}</div>
                    )}
                </div>
            </div>

            <div className="forum-create-post-modal-footer">
                <button
                    type="button"
                    className="forum-create-post-modal-btn forum-create-post-modal-btn-back"
                    onClick={goToPreviousStep}
                    disabled={loading}
                >
                    <ChevronLeft size={16} />
                    Volver
                </button>
                <button
                    type="button"
                    className="forum-create-post-modal-btn forum-create-post-modal-btn-next"
                    onClick={goToNextStep}
                    disabled={loading}
                >
                    Revisar
                    <ChevronRight size={16} />
                </button>
            </div>
        </>
    );

    // Paso 4: Preview con todos los datos + botón Publicar
    const renderPreview = () => {
        const locationName = findLocationName();
        const catName = findCategoryName();
        const submitting = loading || uploadingImages;

        return (
            <>
                {renderFormHeader('Revisá tu post')}
                {renderStepIndicator()}
                <div className="forum-create-post-modal-body">
                    <div className="forum-create-post-modal-step-intro">
                        <h3 className="forum-create-post-modal-step-title">
                            <Eye size={18} />
                            Vista previa
                        </h3>
                        <p className="forum-create-post-modal-step-subtitle">
                            Así verán tu publicación los profesionales. Podés volver atrás para editar antes de publicar.
                        </p>
                    </div>

                    <div className="forum-create-post-modal-preview">
                        <div className="forum-create-post-modal-preview-header">
                            <span className="forum-create-post-modal-preview-badge">{catName || 'Sin categoría'}</span>
                            {locationName && (
                                <span className="forum-create-post-modal-preview-location">
                                    <MapPin size={12} /> {locationName}
                                </span>
                            )}
                        </div>

                        <p className="forum-create-post-modal-preview-description">
                            {formData.description || <em>Sin descripción.</em>}
                        </p>

                        {imagePreviewUrls.length > 0 && (
                            <div className="forum-create-post-modal-preview-images">
                                {imagePreviewUrls.map((url, idx) => (
                                    <img key={idx} src={url} alt={`Preview ${idx + 1}`} />
                                ))}
                            </div>
                        )}

                        {formData.preferredVisitTime && (
                            <div className="forum-create-post-modal-preview-meta">
                                <div className="forum-create-post-modal-preview-meta-item">
                                    <Clock size={14} />
                                    <span>{formData.preferredVisitTime}</span>
                                </div>
                            </div>
                        )}

                        {/* CTA igual al que verán los proveedores en el listado */}
                        <div className="forum-create-post-modal-preview-cta">
                            <button
                                type="button"
                                className="forum-create-post-modal-preview-whatsapp"
                                disabled
                                aria-disabled="true"
                                title="Así verán los proveedores el botón de contacto"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="#ffffff" aria-hidden="true">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787"/>
                                </svg>
                                Contactar
                            </button>
                        </div>
                    </div>

                    <div className="forum-create-post-modal-preview-note">
                        <ShieldCheck size={16} />
                        <span>
                            Tu teléfono no se muestra en el post. Se comparte únicamente con profesionales registrados cuando hacen click en "Contactar".
                        </span>
                    </div>
                </div>

                <div className="forum-create-post-modal-footer">
                    <button
                        type="button"
                        className="forum-create-post-modal-btn forum-create-post-modal-btn-back"
                        onClick={goToPreviousStep}
                        disabled={submitting}
                    >
                        <ChevronLeft size={16} />
                        Volver
                    </button>
                    <button
                        type="button"
                        className={`forum-create-post-modal-btn forum-create-post-modal-btn-submit ${submitting ? 'is-loading' : ''}`}
                        onClick={handleSubmit}
                        disabled={submitting}
                    >
                        {submitting ? (
                            <>
                                <span className="forum-create-post-modal-btn-spinner" aria-hidden="true" />
                                {uploadingImages ? 'Subiendo imágenes...' : 'Publicando...'}
                            </>
                        ) : (
                            <>
                                <Save size={16} />
                                Publicar post
                            </>
                        )}
                    </button>
                </div>
            </>
        );
    };

    return (
        <div
            className={`forum-create-post-modal-overlay ${
                /iPad|iPhone|iPod/.test(navigator.userAgent) ? 'is-ios' : ''
            } ${
                window.innerWidth <= 768 ? 'is-mobile' : ''
            }`}
            onClick={handleOverlayClick}
        >
            <div className="forum-create-post-modal-container">
                {currentStep === 1 && renderIntroduction()}
                {currentStep === 2 && renderStepWhat()}
                {currentStep === 3 && renderStepHow()}
                {currentStep === 4 && renderPreview()}
            </div>
        </div>
    );
};

export default CreatePostModal;
